import { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchOrganizations, buildOrganizationSearchQuery, } from "../api/organizationService";
var PAGE_SIZE = 50;
/** Maps visible column names to CQL entity field names for sorting. */
var SORT_COLUMN_MAP = {
    name: "name",
    status: "status",
};
/**
 * Hook for organizations list page with search, status filtering, sorting, and pagination.
 * Builds CQL queries with proper escaping for safe search.
 * Search is triggered explicitly via handleSearch (form submit / Search button).
 * Filter state is persisted to URL search params so it survives navigation.
 */
var useOrganizationList = function () {
    var ky = useOkapiKy();
    var location = useLocation();
    var history = useHistory();
    // Parse initial filter values from URL search params (read once on mount)
    var initialParams = useMemo(function () {
        var params = new URLSearchParams(location.search);
        return {
            query: params.get("query") || "",
            status: params.get("status") || "ACTIVE",
            offset: parseInt(params.get("offset") || "0", 10) || 0,
            sort: params.get("sort") || "name",
            order: params.get("order") || "ascending",
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    var _a = useState(initialParams.query), query = _a[0], setQuery = _a[1];
    var _b = useState(initialParams.query), appliedQuery = _b[0], setAppliedQuery = _b[1];
    var _c = useState(initialParams.status), statusFilter = _c[0], setStatusFilter = _c[1];
    var _d = useState(initialParams.status), appliedStatusFilter = _d[0], setAppliedStatusFilter = _d[1];
    var _f = useState(initialParams.sort), sortColumn = _f[0], setSortColumn = _f[1];
    var _g = useState(initialParams.order), sortDirection = _g[0], setSortDirection = _g[1];
    var _h = useState(initialParams.offset), pageOffset = _h[0], setPageOffset = _h[1];
    var _j = useState(true), filterPaneIsVisible = _j[0], setFilterPaneIsVisible = _j[1];
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        setAppliedQuery(query);
        setAppliedStatusFilter(statusFilter);
        setPageOffset(0);
    }, [query, statusFilter]);
    // Stable base path: strip any trailing /:id segment so filters are always
    // written to the list URL, not the detail URL (e.g. /organizations/:id).
    var basePath = useMemo(function () { return location.pathname.replace(/\/[0-9a-f-]{36}$/, ""); }, []);
    // Sync applied filter state to URL search params so filters survive back-navigation
    useEffect(function () {
        var params = new URLSearchParams();
        if (appliedQuery)
            params.set("query", appliedQuery);
        if (appliedStatusFilter !== "ACTIVE")
            params.set("status", appliedStatusFilter);
        if (pageOffset > 0)
            params.set("offset", pageOffset.toString());
        if (sortColumn !== "name")
            params.set("sort", sortColumn);
        if (sortDirection !== "ascending")
            params.set("order", sortDirection);
        var search = params.toString();
        history.replace({
            pathname: basePath,
            search: search ? "?".concat(search) : "",
        });
    }, [
        appliedQuery,
        appliedStatusFilter,
        pageOffset,
        sortColumn,
        sortDirection,
        history,
        basePath,
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    var isDefaultFilters = appliedStatusFilter === "ACTIVE" && appliedQuery === "";
    var cqlSortField = SORT_COLUMN_MAP[sortColumn] || "name";
    var cqlQuery = buildOrganizationSearchQuery(appliedQuery, appliedStatusFilter, cqlSortField, sortDirection);
    var _k = useQuery(["czna-organizations", cqlQuery, pageOffset], function () {
        return fetchOrganizations(ky, {
            query: cqlQuery,
            offset: pageOffset,
            limit: PAGE_SIZE,
        });
    }, {
        keepPreviousData: true,
    }), data = _k.data, isLoading = _k.isLoading, isFetching = _k.isFetching, isError = _k.isError, error = _k.error, refetch = _k.refetch;
    var handleClearSearch = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setPageOffset(0);
    }, []);
    var handleClearFilters = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setStatusFilter("ACTIVE");
        setAppliedStatusFilter("ACTIVE");
        setPageOffset(0);
    }, []);
    var handleStatusFilterChange = useCallback(function (value) {
        setStatusFilter(value);
    }, []);
    var handleSort = useCallback(function (_e, _a) {
        var name = _a.name;
        if (!SORT_COLUMN_MAP[name])
            return;
        if (name === sortColumn) {
            setSortDirection(function (prev) {
                return prev === "ascending" ? "descending" : "ascending";
            });
        }
        else {
            setSortColumn(name);
            setSortDirection("ascending");
        }
        setPageOffset(0);
    }, [sortColumn]);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var fetchMoreData = useCallback(function (offset) {
        setPageOffset(offset);
    }, []);
    return {
        query: query,
        setQuery: setQuery,
        pageOffset: pageOffset,
        pageSize: PAGE_SIZE,
        statusFilter: statusFilter,
        handleStatusFilterChange: handleStatusFilterChange,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        handleSort: handleSort,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        handleSearch: handleSearch,
        handleClearSearch: handleClearSearch,
        handleClearFilters: handleClearFilters,
        isDefaultFilters: isDefaultFilters,
        fetchMoreData: fetchMoreData,
        items: (data === null || data === void 0 ? void 0 : data.items) || [],
        // Both fall back to "nothing" when the request failed, so `totalRecords: 0`
        // is NOT evidence that the database is empty -- consumers must consult
        // `isError` before they are allowed to say "0 records" (CZNA-696).
        totalRecords: (data === null || data === void 0 ? void 0 : data.totalRecords) || 0,
        isLoading: isLoading,
        isFetching: isFetching,
        isError: isError,
        error: error,
        refetch: refetch,
    };
};
export default useOrganizationList;
